import {defineStore} from "pinia";
import {computed, ref, reactive} from "vue";
import {useHttpClient} from "@/core/network/httpClient";
import {useToast} from "vue-toastification";
import {useI18n} from "vue-i18n";
import {useModal} from "@/core/components/utils/modals/store/modalStore";
import {ModalState} from "../../../core/components/utils/modals/store/modalStore";

export enum UploadType {
    None = 0,
    SharedUserProfile = 1,
    SharedAdminProfile = 2,
    SharedGrade = 3,
    SharedSubject = 4,
    EnjazPackages = 14,
    EnjazCourses = 15,
    EnjazLessons = 16,


}


export const useImages = defineStore("ImagesStore", () => {
    const api = useHttpClient();
    const {showSuccessModal, showDeleteModal} = useModal();
    const {t} = useI18n();

    const API_URL = "/v1.0/management/Images";

    const state = reactive<{ data: Images[]; selectedData: string[] }>({
        data: [],
        selectedData: [],
    });

    const toast = useToast();
    const loading = ref<boolean>(false);

    const uploadTypesList = computed(() => {
        return [
            {label: t("none"), value: UploadType.None},
            {label: t("user-profile"), value: UploadType.SharedUserProfile},
            {label: t("admin-profile"), value: UploadType.SharedAdminProfile},
            {label: t("grade"), value: UploadType.SharedGrade},
            {label: t("subject"), value: UploadType.SharedSubject},
            {label: t("packages"), value: UploadType.EnjazPackages},
            {label: t("courses"), value: UploadType.EnjazCourses},
            {label: t("lessons"), value: UploadType.EnjazLessons},
        ];
    });

    // fetch data
    const fetchData = async (type: UploadType | null = null) => {
        try {
            loading.value = true;
            const {data: fetchedData} = await api.get(`${API_URL}`, {
                params: {
                    type: type ?? null,
                },
            });
            state.data = fetchedData.content;
            return state;
        } catch (e: any) {
            throw e;
        } finally {
            loading.value = false;
        }
    };

    const create = async (
        imageFile: File,
        uploadType: UploadType = UploadType.None
    ) => {
        try {
            const formData = new FormData();
            formData.append("photo", imageFile!);
            formData.append("type", uploadType!.toString());
            const res = await api.post(API_URL, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            toast.success(res.data.message);
            return res.data.content;
        } catch (e: any) {
            throw e;
        } finally {
            loading.value = false;
        }
    };

    const toggleChecked = (id: string) => {
        if (state.selectedData.includes(id)) {
            state.selectedData = state.selectedData.filter(
                (item_id) => item_id != id
            );
        } else {
            state.selectedData.push(id);
        }
    };

    const deleteImages = async () => {
        const {reason, modalState, adminPassword} = await showDeleteModal({
            title: t("delete-image"),
            message: t("are-you-sure-you-want-to-delete-this-selected-images"),
        });
        if (modalState === ModalState.Canceled) return;
        loading.value = true;
        Promise.all(
            state.selectedData.map(async (id) => {
                await api.request({
                    url: `${API_URL}/${id}`,
                    method: "delete",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    data: {
                        reason,
                        adminPassword,
                    },
                });
            })
        )
            .then(async () => {
                state.selectedData = [];
                await fetchData();
                showSuccessModal(t("delete-image"), t("image-deleted-successfully"));
            })
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                loading.value = false;
            });
    };

    const deleteImage = async (id: string) => {
        const {reason, modalState, adminPassword} = await showDeleteModal({
            title: t("delete-image"),
            message: t("are-you-sure-you-want-to-delete-this-image"),
        });
        if (modalState === ModalState.Canceled) return;
        loading.value = true;
        try {
            await api.request({
                url: `${API_URL}/${id}`,
                method: "delete",
                headers: {
                    "Content-Type": "application/json",
                },
                data: {
                    reason,
                    adminPassword,
                },
            });
            await fetchData();
            showSuccessModal(t("delete-image"), t("image-deleted-successfully"));
        } catch (e: any) {
            throw e;
        } finally {
            loading.value = false;
        }
    };

    const getUploadType = (type: UploadType) => {
        switch (type) {
            case UploadType.SharedUserProfile:
                return t("user-profile");
            case UploadType.SharedAdminProfile:
                return t("admin-profile");
            case UploadType.SharedGrade:
                return t("grade");
            case UploadType.SharedSubject:
                return t("subject");
            case UploadType.EnjazPackages:
                return t("packages");
            case UploadType.EnjazCourses:
                return t("courses");
            case UploadType.EnjazLessons:
                return t("lessons");
            default:
                return t("none");
        }
    };

    return {
        state,
        data: computed(() => state.data),
        fetchData,
        loading: computed(() => loading.value),
        API_URL,
        create,
        getUploadType,
        deleteImage,
        uploadTypesList,
        toggleChecked,
        deleteImages,
    };
});
